import React from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Typography,
} from "@mui/material";
import { FileEditorStore } from "../../stores/FileEditorStore";
import { DocumentEditor } from "../../../entities/common/controls/DocumentEditor";

export interface FileEditorDialogProps {
  store: FileEditorStore;
  editor?: React.ComponentType<{
    value: string;
    onChange: (value: string) => void;
    error?: string | null;
    metadata?: any;
    renderedContent?: string;
    templateContent?: string;
    opened?: boolean;
    initialMode?: string;
    onTemplateChange?: (content: string) => void;
  }>;
  height?: string;
  width?: "xs" | "sm" | "md" | "lg" | "xl" | false;
}

export const FileEditorDialog: React.FC<FileEditorDialogProps> = observer(
  ({
    store,
    editor: EditorComponent = DocumentEditor,
    height = "500px",
    width = "lg",
  }) => {
    if (!store.isOpen) return null;

    const displayTitle = store.metadata?.displayName || "Editor";

    return (
      <Dialog
        open={store.isOpen}
        onClose={() => store.cancel()}
        maxWidth={width}
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {displayTitle}
            {!store.readOnly && store.hasChanges && (
              <Chip label="Cambios sin guardar" size="small" color="warning" />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {store.isLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={3}
            >
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Cargando contenido...</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", height }}>
                <EditorComponent
                  value={store.content}
                  onChange={(newContent: string) =>
                    store.setContent(newContent)
                  }
                  error={store.error}
                  metadata={store.metadata}
                  // Props específicos para DocumentEditor (retrocompatibilidad)
                  renderedContent={store.content}
                  templateContent={store.content}
                  opened={true}
                  initialMode={store.readOnly ? "readonly" : "edit"}
                  onTemplateChange={(content: string) =>
                    store.setContent(content)
                  }
                />
              </Box>

              {store.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {store.error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => store.cancel()} disabled={store.isLoading}>
            {store.readOnly ? "Cerrar" : "Cancelar"}
          </Button>
          {!store.readOnly && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => store.save()}
              disabled={!store.canSave || store.isLoading}
            >
              {store.isLoading ? "Guardando..." : "Guardar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);
